import { usePostHog } from '@posthog/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'components/ui/button';
import type { AiChatSurface } from '../hooks/useAiChatController';
import { formatRelativeTimeFromNow } from '../utils/relativeTime';

interface AiChatResumeBannerProps {
    surface: AiChatSurface;
    /** When the resumed conversation was last active, for the "from 3 minutes ago" phrasing. */
    resumedAt: number;
    onNewChat: () => void;
}

/**
 * Slim strip under a surface's header saying the conversation on screen was auto-resumed, with the
 * other option one click away. Not dismissable on its own and not on a timer: it stays until the
 * user acts — sends, starts a new chat, or picks another thread — which the owning surface tracks.
 */
const AiChatResumeBanner: React.FC<AiChatResumeBannerProps> = ({ surface, resumedAt, onNewChat }) => {
    const intl = useIntl();
    const posthog = usePostHog();

    return (
        <div className="border-border bg-muted text-muted-foreground text-caption flex shrink-0 items-center justify-between gap-2 border-b px-3 py-1">
            <span>
                <FormattedMessage
                    id="ai-chat.resume.banner"
                    values={{ time: formatRelativeTimeFromNow(intl, resumedAt) }}
                />
            </span>
            <Button
                type="button"
                variant="ghost"
                size="xs"
                className="shrink-0"
                onClick={() => {
                    posthog.capture('ai_chat_resume_banner_new_chat', { surface });
                    onNewChat();
                }}
            >
                <FormattedMessage id="ai-chat.new-chat" />
            </Button>
        </div>
    );
};

export default AiChatResumeBanner;
